<!--
  OrderLinkStatsView.vue — 链接统计
  按日期 + 资产维度展示签约、支付、首付、续费统计。
  功能：日期筛选、资产筛选、趋势折线图、分页明细。
  接口：
    GET /v1/analytics/order/stats/daily/asset/payment
    GET /v1/analytics/order/stats/asset/list
-->
<template>
  <div class="link-stat-page">
    <a-card :bordered="false" style="margin-bottom: 16px">
      <a-form layout="inline" :model="searchForm">
        <a-form-item label="开始日期">
          <a-date-picker
            v-model:value="searchForm.startDate"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
            placeholder="开始日期"
            style="width: 140px"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="结束日期">
          <a-date-picker
            v-model:value="searchForm.endDate"
            value-format="YYYY-MM-DD"
            :allow-clear="true"
            placeholder="结束日期"
            style="width: 140px"
            @change="clearQuickDate"
          />
        </a-form-item>
        <a-form-item label="快捷日期">
          <a-radio-group
            v-model:value="quickDateValue"
            option-type="button"
            button-style="solid"
            class="quick-date-group"
            @change="handleQuickDateChange"
          >
            <a-radio-button
              v-for="item in quickDateOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </a-radio-button>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="资产">
          <a-select
            v-model:value="searchForm.asset"
            style="width: 320px"
            placeholder="全部资产"
            allow-clear
            show-search
            option-filter-prop="label"
            :loading="assetLoading"
            :options="assetOptions"
            @change="handleAssetChange"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button
              type="primary"
              :loading="loading || chartLoading"
              @click="handleSearch"
            >查询</a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card :bordered="false" title="链接订单趋势" style="margin-bottom: 16px">
      <a-spin :spinning="chartLoading">
        <template v-if="chartPoints.length">
          <v-chart :option="chartOption" autoresize style="height: 360px" />
          <div class="chart-summary" v-if="latestPoint && totalPoint">
            <div class="summary-column">
              <div class="summary-title">最近日期：{{ latestPoint.statDate }}</div>
              <div class="summary-metrics">
                <span v-for="metric in summaryMetrics" :key="metric.key">
                  {{ metric.label }}：<b :style="{ color: metric.color }">{{
                    formatCount(latestPoint[metric.key])
                  }}</b>
                </span>
              </div>
            </div>
            <div class="summary-column">
              <div class="summary-title">合计</div>
              <div class="summary-metrics">
                <span v-for="metric in summaryMetrics" :key="metric.key">
                  {{ metric.label }}：<b :style="{ color: metric.color }">{{
                    formatCount(totalPoint[metric.key])
                  }}</b>
                </span>
              </div>
            </div>
          </div>
        </template>
        <a-empty v-else description="暂无链接统计数据" class="chart-empty" />
      </a-spin>
    </a-card>

    <a-card :bordered="false" title="链接统计明细">
      <a-table
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        row-key="rowKey"
        :scroll="{ x: 'max-content' }"
        size="small"
        bordered
        @change="handleTableChange"
      />
    </a-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { message } from "ant-design-vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";
import { analyticsUrl } from "@/utils/api";
import { get } from "@/utils/request";

use([CanvasRenderer, LineChart, GridComponent, TooltipComponent, LegendComponent]);

const metrics = [
  { key: "agreementOrderCount", label: "签约下单", color: "#1677ff" },
  { key: "agreementSuccessCount", label: "签约成功", color: "#52c41a" },
  { key: "payOrderCount", label: "下单支付", color: "#fa8c16" },
  { key: "paySuccessCount", label: "支付成功", color: "#13c2c2" },
  { key: "payFailedCount", label: "支付失败", color: "#ff4d4f" },
  { key: "firstPaySuccessCount", label: "首次支付成功", color: "#722ed1" },
  { key: "renewPaySuccessCount", label: "续费成功", color: "#eb2f96" },
  { key: "insufficientBalanceCount", label: "余额不足", color: "#faad14" },
];

const summaryMetrics = metrics;

const quickDateOptions = [
  { label: "今天", value: "today", startOffset: 0, endOffset: 0 },
  { label: "昨天", value: "yesterday", startOffset: 1, endOffset: 1 },
  { label: "最近2天", value: "last_2_days", startOffset: 1, endOffset: 0 },
  { label: "最近3天", value: "last_3_days", startOffset: 2, endOffset: 0 },
  { label: "最近5天", value: "last_5_days", startOffset: 4, endOffset: 0 },
  { label: "最近7天", value: "last_7_days", startOffset: 6, endOffset: 0 },
  { label: "最近30天", value: "last_30_days", startOffset: 29, endOffset: 0 },
];

const loading = ref(false);
const chartLoading = ref(false);
const assetLoading = ref(false);
const tableData = ref([]);
const chartRows = ref([]);
const assets = ref([]);
const quickDateValue = ref(null);

const searchForm = reactive({
  startDate: "",
  endDate: "",
  asset: undefined,
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
  showTotal: (total) => `共 ${total} 条`,
});

const nowrap = { style: { whiteSpace: "nowrap" } };
const numberCell = {
  style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
};

const toNumber = (value) => Number(value || 0);

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDaysAgoDate = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return formatDate(date);
};

const formatCount = (value) =>
  value === null || value === undefined
    ? "-"
    : Number(value).toLocaleString("zh-CN");

const formatDayLabel = (value) => {
  const s = String(value || "");
  if (s.length >= 10) return s.slice(5, 10);
  return s || "-";
};

const assetNameMap = computed(() => {
  const map = new Map();
  assets.value.forEach((item) => {
    if (item.asset) map.set(String(item.asset), item.name || "");
  });
  return map;
});

const assetOptions = computed(() =>
  assets.value.map((item) => ({
    label: item.name ? `${item.name} (${item.asset})` : item.asset,
    value: String(item.asset),
  }))
);

const getAssetName = (asset) => assetNameMap.value.get(String(asset)) || "-";

const buildParams = (pageIdx, pageSize) => {
  const params = { pageIdx, pageSize };
  if (searchForm.startDate) params.startDate = searchForm.startDate;
  if (searchForm.endDate) params.endDate = searchForm.endDate;
  if (searchForm.asset) params.asset = searchForm.asset;
  return params;
};

const validateSearch = () => {
  if (
    searchForm.startDate &&
    searchForm.endDate &&
    searchForm.startDate > searchForm.endDate
  ) {
    message.warning("开始日期不能晚于结束日期");
    return false;
  }
  return true;
};

const normalizeRows = (rows) =>
  (rows || []).map((row, index) => ({
    ...row,
    rowKey: `${row.statDate || "date"}_${row.asset || "asset"}_${index}`,
  }));

const chartPoints = computed(() => {
  const map = new Map();
  for (const row of chartRows.value) {
    const statDate = row.statDate;
    if (!statDate) continue;
    if (!map.has(statDate)) {
      const point = { statDate, label: formatDayLabel(statDate) };
      metrics.forEach((metric) => {
        point[metric.key] = 0;
      });
      map.set(statDate, point);
    }
    const point = map.get(statDate);
    metrics.forEach((metric) => {
      point[metric.key] += toNumber(row[metric.key]);
    });
  }
  return Array.from(map.values()).sort((a, b) =>
    a.statDate.localeCompare(b.statDate)
  );
});

const latestPoint = computed(
  () => chartPoints.value[chartPoints.value.length - 1] || null
);

const totalPoint = computed(() =>
  chartPoints.value.reduce((sum, point) => {
    metrics.forEach((metric) => {
      sum[metric.key] += point[metric.key];
    });
    return sum;
  }, metrics.reduce((obj, metric) => ({ ...obj, [metric.key]: 0 }), {}))
);

const chartOption = computed(() => {
  const labels = chartPoints.value.map((point) => point.label);
  return {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "line" },
      formatter: (params) => {
        const point = chartPoints.value[params[0]?.dataIndex];
        const title = point?.statDate || params[0]?.axisValue || "";
        return (
          title +
          params
            .map(
              (item) =>
                `<br/>${item.marker}${item.seriesName}：${formatCount(
                  item.value
                )}`
            )
            .join("")
        );
      },
    },
    legend: {
      type: "scroll",
      data: metrics.map((metric) => metric.label),
      top: 0,
      selected: {
        支付失败: false,
        余额不足: false,
      },
    },
    grid: { top: 58, left: 0, right: 8, bottom: 28, containLabel: true },
    xAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: "#d9d9d9" } },
      axisTick: { alignWithLabel: true },
      axisLabel: { fontSize: 11, rotate: 30 },
    },
    yAxis: {
      type: "value",
      name: "数量",
      axisLabel: {
        fontSize: 11,
        formatter: (value) => Number(value).toLocaleString("zh-CN"),
      },
    },
    series: metrics.map((metric) => ({
      name: metric.label,
      type: "line",
      smooth: true,
      showSymbol: false,
      data: chartPoints.value.map((point) => point[metric.key]),
      itemStyle: { color: metric.color },
      lineStyle:
        metric.key === "renewPaySuccessCount" ? { type: "dashed" } : undefined,
    })),
  };
});

const columns = [
  {
    title: "统计日期",
    dataIndex: "statDate",
    key: "statDate",
    width: 110,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "资产ID",
    dataIndex: "asset",
    key: "asset",
    width: 150,
    fixed: "left",
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "资产名称",
    key: "assetName",
    width: 220,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
    customRender: ({ record }) => getAssetName(record.asset),
  },
  ...metrics.map((metric) => ({
    title: metric.label,
    dataIndex: metric.key,
    key: metric.key,
    width: 120,
    align: "center",
    customCell: () => numberCell,
    customHeaderCell: () => nowrap,
    customRender: ({ text }) => formatCount(text),
  })),
  {
    title: "更新时间",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
  {
    title: "创建时间",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 165,
    customCell: () => nowrap,
    customHeaderCell: () => nowrap,
  },
];

const fetchAssets = async () => {
  assetLoading.value = true;
  try {
    const json = await get(analyticsUrl("/order/stats/asset/list"));
    const rows = Array.isArray(json.data?.data)
      ? json.data.data
      : Array.isArray(json.data)
      ? json.data
      : [];
    assets.value = rows
      .filter((item) => item.asset)
      .map((item) => ({
        asset: String(item.asset),
        name: item.name || "",
      }));
  } catch {
    message.error("资产列表接口请求失败");
  } finally {
    assetLoading.value = false;
  }
};

const fetchTable = async (pageIdx = 1, pageSize = 20) => {
  loading.value = true;
  try {
    const json = await get(
      analyticsUrl("/order/stats/daily/asset/payment"),
      buildParams(pageIdx, pageSize)
    );
    if (json.code === 200 && json.data) {
      const data = json.data;
      tableData.value = normalizeRows(data.data);
      pagination.total = data.dataSum ?? data.total ?? 0;
      pagination.current = data.pageIdx ?? pageIdx;
      pagination.pageSize = data.pageSize ?? pageSize;
    } else {
      message.warning("查询失败：" + (json.content || "未知错误"));
    }
  } catch {
    message.error("接口请求失败");
  } finally {
    loading.value = false;
  }
};

const fetchChart = async () => {
  chartLoading.value = true;
  try {
    const json = await get(
      analyticsUrl("/order/stats/daily/asset/payment"),
      buildParams(1, 500)
    );
    if (json.code === 200 && json.data) {
      chartRows.value = json.data.data || [];
    } else {
      chartRows.value = [];
      message.warning("趋势数据查询失败：" + (json.content || "未知错误"));
    }
  } catch {
    chartRows.value = [];
    message.error("趋势数据接口请求失败");
  } finally {
    chartLoading.value = false;
  }
};

const fetchData = async (pageIdx = 1, pageSize = 20, refreshChart = true) => {
  if (refreshChart) {
    await Promise.all([fetchTable(pageIdx, pageSize), fetchChart()]);
  } else {
    await fetchTable(pageIdx, pageSize);
  }
};

const handleSearch = () => {
  if (!validateSearch()) return;
  fetchData(1, pagination.pageSize);
};

const handleAssetChange = () => {
  handleSearch();
};

const clearQuickDate = () => {
  quickDateValue.value = null;
};

const handleQuickDateChange = ({ target }) => {
  const option = quickDateOptions.find((item) => item.value === target.value);
  if (!option) return;
  searchForm.startDate = getDaysAgoDate(option.startOffset);
  searchForm.endDate = getDaysAgoDate(option.endOffset);
  handleSearch();
};

const handleReset = () => {
  searchForm.startDate = "";
  searchForm.endDate = "";
  searchForm.asset = undefined;
  quickDateValue.value = null;
  fetchData(1, pagination.pageSize);
};

const handleTableChange = (pag) => {
  fetchData(pag.current, pag.pageSize, false);
};

onMounted(() => {
  fetchAssets();
  fetchData();
});
</script>

<style scoped>
.link-stat-page :deep(.ant-card-head-title) {
  min-height: 24px;
}

.quick-date-group {
  white-space: nowrap;
}

.chart-summary {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  justify-content: start;
  gap: 12px 32px;
  width: max-content;
  max-width: 100%;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  color: #666;
  font-size: 12px;
  line-height: 1.6;
}

.summary-column {
  min-width: max-content;
}

.summary-title {
  margin-bottom: 6px;
  color: #262626;
  font-size: 13px;
  font-weight: 600;
}

.summary-metrics {
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 2px 16px;
}

.summary-metrics span {
  white-space: nowrap;
}

.chart-summary b {
  font-weight: 600;
}

.chart-empty {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@media (max-width: 768px) {
  .chart-summary,
  .summary-metrics {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .summary-column {
    min-width: 0;
  }
}
</style>
